import { Fragment, type FunctionComponent } from "react";
import { Hero } from "@App/Components/Hero";
import { ContactUs } from "@App/Components/ContactUs";
import { TriangleDivider } from "@App/Components/TriangleDivider";
import { Stepper } from "@App/Components/Stepper/Stepper";
import { useServices } from "@App/hooks/api/useServices";
import { useTranslation } from "react-i18next";
import { useHome } from "@App/hooks/useHome";
import { DriverDataServiceStep } from "@App/Pages/Partner/Partials/DriverDataServiceStep";
import { useDriverData } from "@App/hooks/useDriverData";
import { LocationStepDefinition } from "@App/Components/molecules/whitelabel/home/stepDefinitions/LocationStepDefinition";
import { useLocationStepDefinition } from "@App/hooks/whitelabel/home/stepper/useLocationStepDefinition";
import { useMessageStepDefinition } from "@App/hooks/whitelabel/home/stepper/useMessageStepDefinition";
import { MessageStepDefinition } from "@App/Components/molecules/whitelabel/home/stepDefinitions/MessageStepDefinition";
import { MileageStepDefinition } from "@App/Components/molecules/whitelabel/home/stepDefinitions/MileageStepDefinition";
import { useMileageStepDefinition } from "@App/hooks/whitelabel/home/stepper/useMileageStepDefinition";
import { Step } from "@App/Components/Stepper/Step";
import { getServiceNameLabel } from "@App/utils/getServiceNameLabel";

export type HomeProps = {
  onSubmit: () => void;
};

export const Home: FunctionComponent<HomeProps> = ({ onSubmit }: HomeProps) => {
  const { t } = useTranslation();

  const { error, services } = useServices();

  const {
    currentStep,
    setCurrentStep,
    togglePremiumService,
    toggled,
    nextStep,
    organization,
    service,
    premiumServices,
    simpleServiceSelect,
    serviceToggled,
  } = useHome();

  // step definitions
  const mileageStepProps = useMileageStepDefinition(nextStep);
  const locationStepProps = useLocationStepDefinition(onSubmit);

  const {
    requiredServices,
    requiredPremiumServices,
    optionalServices,
    optionalPremiumServices,
    prohibitedServices,
    prohibitedPremiumServices,
  } = useDriverData();

  if (error) {
    console.log(error);
  }

  const serviceNameLabel = getServiceNameLabel(organization, service, premiumServices);

  return (
    <Fragment>
      <Hero
        organization={organization}
        title={t("hero.title", { organization: organization?.name })}
        subtitle={t("hero.subtitle", { organization: organization?.name })}
        backgroundImage={
          organization?.settings?.hero.background_image_url ??
          "/images/brand/arval-banner-darkened.jpg"
        }
        backgroundPosition={"bg-center"}
      />
      <div className="bg-white px-5 pt-5 container">
        <Stepper currentStep={currentStep} onChange={setCurrentStep}>
          <MileageStepDefinition {...mileageStepProps} />
          <Step
            title={t("chooseSubServices")}
            completedTitle={"Services: "}
            completedGoBackLinkTitle={serviceNameLabel}
          >
            <DriverDataServiceStep
              requiredServices={requiredServices}
              requiredPremiumServices={requiredPremiumServices}
              optionalServices={optionalServices}
              optionalPremiumServices={optionalPremiumServices}
              prohibitedServices={prohibitedServices}
              prohibitedPremiumServices={prohibitedPremiumServices}
              toggled={toggled}
              serviceToggled={serviceToggled}
              premiumServices={premiumServices ?? []}
              selectService={simpleServiceSelect}
              services={services}
              service={service}
              togglePremiumService={togglePremiumService}
              nextStep={nextStep}
            />
          </Step>
          <MessageStepDefinition {...useMessageStepDefinition(nextStep)} />
          <LocationStepDefinition {...locationStepProps} />
        </Stepper>
      </div>
      <div className="bg-neutral-100">
        <TriangleDivider direction="left" background={"bg-white"} />
        <div className="px-5 py-5 container md:pb-20">
          <div className="md:grid md:grid-cols-[50%_50%]">
            <div className="pt-5 md:pt-0 md:ml-12">
              <ContactUs imageHidden={true} />
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};
